import { create } from "zustand";
import {
  DEFAULT_BPM,
  DEFAULT_SUBDIVISION_FLAGS,
  DRUM_ROWS,
  MAX_BPM,
  MIN_BPM,
  cycleSubdivisionBase,
  stepsPerBar,
  type SubdivisionFlags,
} from "../constants/layout";
import type { KeyboardTrackId, PitchClass, TrackId } from "../constants/scale";
import { scaleRowCount, scaleSemitonesDesc } from "../constants/scale";
import type { DrumId } from "../constants/drums";
import type { FxParam } from "../audio/fxBus";
import { DEFAULT_DECAY, type EnvelopeParam } from "../audio/envelope";
import { clamp } from "../utils/clamp";
import { remapRowToSteps } from "./gridResize";

export type FxOrEnvelopeParam = FxParam | EnvelopeParam;

export interface FxState {
  volume: number;
  fuzz: number;
  delay: number;
  reverb: number;
  decay: number;
}

const DEFAULT_FX: FxState = {
  volume: 0.85,
  fuzz: 0,
  delay: 0,
  reverb: 0,
  decay: DEFAULT_DECAY,
};

export type Pattern = boolean[][];
export type MelodyGrid = boolean[][];
export type MelodyGrids = Record<KeyboardTrackId, MelodyGrid>;
export type TrackMutes = Record<TrackId, boolean>;
export type DrumMutes = Record<DrumId, boolean>;
export type SubdivisionMap = Record<TrackId, SubdivisionFlags>;
export type StepCursor = Record<TrackId, number>;

export { DEFAULT_SUBDIVISION_FLAGS as DEFAULT_SUBDIVISION };
export type { SubdivisionFlags };

function defaultSubdivisions(): SubdivisionMap {
  return {
    drums: { ...DEFAULT_SUBDIVISION_FLAGS },
    keys: { ...DEFAULT_SUBDIVISION_FLAGS },
    bass: { ...DEFAULT_SUBDIVISION_FLAGS },
    lead: { ...DEFAULT_SUBDIVISION_FLAGS },
  };
}

function defaultStepCursor(): StepCursor {
  return { drums: 0, keys: 0, bass: 0, lead: 0 };
}

function emptyRow(steps: number): boolean[] {
  return Array<boolean>(steps).fill(false);
}

function emptyPattern(steps: number): Pattern {
  return Array.from({ length: DRUM_ROWS }, () => emptyRow(steps));
}

function emptyMelodyGrid(rowCount: number, steps: number): MelodyGrid {
  return Array.from({ length: rowCount }, () => emptyRow(steps));
}

function emptyMelodyGrids(
  rowCount: number,
  stepsByTrack: Record<KeyboardTrackId, number>,
): MelodyGrids {
  return {
    keys: emptyMelodyGrid(rowCount, stepsByTrack.keys),
    bass: emptyMelodyGrid(rowCount, stepsByTrack.bass),
    lead: emptyMelodyGrid(rowCount, stepsByTrack.lead),
  };
}

/**
 * Rebuild a melody grid when the blue-note configuration changes. Armed cells
 * on rows that still exist in the new scale are preserved; new rows are empty.
 */
function resizeMelodyGridBlues(
  grid: MelodyGrid,
  oldBlues: { blueFlat3: boolean; blueFlat5: boolean },
  newBlues: { blueFlat3: boolean; blueFlat5: boolean },
  steps: number,
): MelodyGrid {
  const oldSemitones = scaleSemitonesDesc(oldBlues);
  const newSemitones = scaleSemitonesDesc(newBlues);
  if (
    oldSemitones.length === newSemitones.length &&
    oldSemitones.every((s, i) => s === newSemitones[i])
  ) {
    return grid;
  }
  return newSemitones.map((semi) => {
    const oldIdx = oldSemitones.indexOf(semi);
    if (oldIdx >= 0 && grid[oldIdx]) {
      return [...grid[oldIdx]];
    }
    return emptyRow(steps);
  });
}

function resizeAllMelodyGridsBlues(
  grids: MelodyGrids,
  oldBlues: { blueFlat3: boolean; blueFlat5: boolean },
  newBlues: { blueFlat3: boolean; blueFlat5: boolean },
): MelodyGrids {
  return {
    keys: resizeMelodyGridBlues(
      grids.keys,
      oldBlues,
      newBlues,
      grids.keys[0]?.length ?? 0,
    ),
    bass: resizeMelodyGridBlues(
      grids.bass,
      oldBlues,
      newBlues,
      grids.bass[0]?.length ?? 0,
    ),
    lead: resizeMelodyGridBlues(
      grids.lead,
      oldBlues,
      newBlues,
      grids.lead[0]?.length ?? 0,
    ),
  };
}

function defaultFxAll(): Record<TrackId, FxState> {
  return {
    drums: { ...DEFAULT_FX },
    keys: { ...DEFAULT_FX },
    bass: { ...DEFAULT_FX },
    lead: { ...DEFAULT_FX },
  };
}

function defaultTrackMutes(): TrackMutes {
  return { drums: false, keys: false, bass: false, lead: false };
}

function defaultDrumMutes(): DrumMutes {
  return { kick: false, snare: false, hat: false, clap: false, tom: false };
}

const DEFAULT_ROOT: PitchClass = "C";
const DEFAULT_ROWS = scaleRowCount({ blueFlat3: false, blueFlat5: false });
const DEFAULT_STEPS = stepsPerBar(DEFAULT_SUBDIVISION_FLAGS);

export interface InstrumentState {
  playing: boolean;
  bpm: number;
  step: StepCursor;
  pattern: Pattern;
  melodyGrids: MelodyGrids;
  fx: Record<TrackId, FxState>;
  trackMutes: TrackMutes;
  drumMutes: DrumMutes;
  subdivisions: SubdivisionMap;
  openFxPanel: TrackId | null;
  advancedOpen: boolean;
  rootPitch: PitchClass;
  blueFlat3: boolean;
  blueFlat5: boolean;
  activeTrack: TrackId;

  setPlaying(playing: boolean): void;
  setStep(track: TrackId, step: number): void;
  resetSteps(): void;
  setBpm(bpm: number): void;
  toggleStep(row: number, step: number): void;
  armDrumRange(row: number, fromStep: number, toStep: number): void;
  setFx(track: TrackId, param: FxOrEnvelopeParam, value: number): void;
  toggleFxPanel(track: TrackId): void;
  toggleMelodyCell(track: KeyboardTrackId, row: number, step: number): void;
  armMelodyRange(
    track: KeyboardTrackId,
    row: number,
    fromStep: number,
    toStep: number,
  ): void;
  clearMelody(track: KeyboardTrackId): void;
  clearTrack(track: TrackId): void;
  toggleTrackMute(track: TrackId): void;
  toggleDrumMute(drum: DrumId): void;
  cycleSubdivisionBase(track: TrackId): void;
  toggleSubdivisionTriplet(track: TrackId): void;
  openAdvanced(): void;
  closeAdvanced(): void;
  setRoot(root: PitchClass): void;
  setBlueFlat3(enabled: boolean): void;
  setBlueFlat5(enabled: boolean): void;
  setActiveTrack(track: TrackId): void;
  clearAll(): void;
}

export const useInstrumentStore = create<InstrumentState>((set) => ({
  playing: false,
  bpm: DEFAULT_BPM,
  step: defaultStepCursor(),
  pattern: emptyPattern(DEFAULT_STEPS),
  melodyGrids: emptyMelodyGrids(DEFAULT_ROWS, {
    keys: DEFAULT_STEPS,
    bass: DEFAULT_STEPS,
    lead: DEFAULT_STEPS,
  }),
  fx: defaultFxAll(),
  trackMutes: defaultTrackMutes(),
  drumMutes: defaultDrumMutes(),
  subdivisions: defaultSubdivisions(),
  openFxPanel: null,
  advancedOpen: false,
  rootPitch: DEFAULT_ROOT,
  blueFlat3: false,
  blueFlat5: false,
  activeTrack: "drums",

  setPlaying: (playing) =>
    set((s) => ({
      playing,
      step: playing ? s.step : defaultStepCursor(),
    })),
  setStep: (track, step) =>
    set((s) => ({ step: { ...s.step, [track]: step } })),
  resetSteps: () => set({ step: defaultStepCursor() }),
  setBpm: (bpm) => set({ bpm: clamp(Math.round(bpm), MIN_BPM, MAX_BPM) }),
  toggleStep: (row, step) =>
    set((s) => {
      const drumSteps = stepsPerBar(s.subdivisions.drums);
      if (row < 0 || row >= DRUM_ROWS || step < 0 || step >= drumSteps)
        return s;
      const next = s.pattern.map((r, i) =>
        i === row ? r.map((cell, j) => (j === step ? !cell : cell)) : r,
      );
      return { pattern: next };
    }),
  armDrumRange: (row, fromStep, toStep) =>
    set((s) => {
      const drumSteps = stepsPerBar(s.subdivisions.drums);
      if (row < 0 || row >= DRUM_ROWS) return s;
      const lo = Math.max(0, Math.min(fromStep, toStep));
      const hi = Math.min(drumSteps - 1, Math.max(fromStep, toStep));
      if (lo > hi) return s;
      const next = s.pattern.map((r, i) =>
        i === row ? r.map((cell, j) => (j >= lo && j <= hi ? true : cell)) : r,
      );
      return { pattern: next };
    }),
  setFx: (track, param, value) =>
    set((s) => {
      const v = clamp(value, 0, 1);
      return { fx: { ...s.fx, [track]: { ...s.fx[track], [param]: v } } };
    }),
  toggleFxPanel: (track) =>
    set((s) => ({ openFxPanel: s.openFxPanel === track ? null : track })),
  toggleMelodyCell: (track, row, step) =>
    set((s) => {
      const grid = s.melodyGrids[track];
      const trackSteps = grid[0]?.length ?? 0;
      if (step < 0 || step >= trackSteps) return s;
      if (row < 0 || row >= grid.length) return s;
      const wasArmed = grid[row][step];
      const polyphonic = track === "keys";
      const next = grid.map((r, rowIdx) =>
        r.map((cell, colIdx) => {
          if (colIdx !== step) return cell;
          if (rowIdx === row) return !wasArmed;
          if (polyphonic) return cell;
          return wasArmed ? cell : false;
        }),
      );
      return { melodyGrids: { ...s.melodyGrids, [track]: next } };
    }),
  armMelodyRange: (track, row, fromStep, toStep) =>
    set((s) => {
      const grid = s.melodyGrids[track];
      const trackSteps = grid[0]?.length ?? 0;
      if (row < 0 || row >= grid.length) return s;
      const lo = Math.max(0, Math.min(fromStep, toStep));
      const hi = Math.min(trackSteps - 1, Math.max(fromStep, toStep));
      if (lo > hi) return s;
      const polyphonic = track === "keys";
      const next = grid.map((r, rowIdx) =>
        r.map((cell, colIdx) => {
          if (colIdx < lo || colIdx > hi) return cell;
          if (rowIdx === row) return true;
          // Mono tracks: arming a range on one row clears every other row's
          // cells in that range, matching toggleMelodyCell's exclusivity.
          if (polyphonic) return cell;
          return false;
        }),
      );
      return { melodyGrids: { ...s.melodyGrids, [track]: next } };
    }),
  clearMelody: (track) =>
    set((s) => {
      const rows = s.melodyGrids[track].length;
      const steps = s.melodyGrids[track][0]?.length ?? 0;
      return {
        melodyGrids: {
          ...s.melodyGrids,
          [track]: emptyMelodyGrid(rows, steps),
        },
      };
    }),
  clearTrack: (track) =>
    set((s) => {
      if (track === "drums") {
        const steps = s.pattern[0]?.length ?? DEFAULT_STEPS;
        return { pattern: emptyPattern(steps) };
      }
      const rows = s.melodyGrids[track].length;
      const steps = s.melodyGrids[track][0]?.length ?? DEFAULT_STEPS;
      return {
        melodyGrids: {
          ...s.melodyGrids,
          [track]: emptyMelodyGrid(rows, steps),
        },
      };
    }),
  toggleTrackMute: (track) =>
    set((s) => ({
      trackMutes: { ...s.trackMutes, [track]: !s.trackMutes[track] },
    })),
  toggleDrumMute: (drum) =>
    set((s) => ({
      drumMutes: { ...s.drumMutes, [drum]: !s.drumMutes[drum] },
    })),
  cycleSubdivisionBase: (track) =>
    set((s) => {
      const current = s.subdivisions[track];
      const nextFlags: SubdivisionFlags = {
        ...current,
        base: cycleSubdivisionBase(current.base),
      };
      const nextSteps = stepsPerBar(nextFlags);
      return applySubdivisionChange(s, track, nextFlags, nextSteps);
    }),
  toggleSubdivisionTriplet: (track) =>
    set((s) => {
      const current = s.subdivisions[track];
      const nextFlags: SubdivisionFlags = {
        ...current,
        triplet: !current.triplet,
      };
      const nextSteps = stepsPerBar(nextFlags);
      return applySubdivisionChange(s, track, nextFlags, nextSteps);
    }),
  openAdvanced: () => set({ advancedOpen: true }),
  closeAdvanced: () => set({ advancedOpen: false }),
  setRoot: (root) =>
    set((s) => {
      if (s.rootPitch === root) return s;
      return { rootPitch: root };
    }),
  setBlueFlat3: (enabled) =>
    set((s) => {
      if (s.blueFlat3 === enabled) return s;
      const oldBlues = { blueFlat3: s.blueFlat3, blueFlat5: s.blueFlat5 };
      const newBlues = { blueFlat3: enabled, blueFlat5: s.blueFlat5 };
      return {
        blueFlat3: enabled,
        melodyGrids: resizeAllMelodyGridsBlues(
          s.melodyGrids,
          oldBlues,
          newBlues,
        ),
      };
    }),
  setBlueFlat5: (enabled) =>
    set((s) => {
      if (s.blueFlat5 === enabled) return s;
      const oldBlues = { blueFlat3: s.blueFlat3, blueFlat5: s.blueFlat5 };
      const newBlues = { blueFlat3: s.blueFlat3, blueFlat5: enabled };
      return {
        blueFlat5: enabled,
        melodyGrids: resizeAllMelodyGridsBlues(
          s.melodyGrids,
          oldBlues,
          newBlues,
        ),
      };
    }),
  setActiveTrack: (track) => set({ activeTrack: track }),
  clearAll: () =>
    set({
      pattern: emptyPattern(DEFAULT_STEPS),
      melodyGrids: emptyMelodyGrids(DEFAULT_ROWS, {
        keys: DEFAULT_STEPS,
        bass: DEFAULT_STEPS,
        lead: DEFAULT_STEPS,
      }),
      step: defaultStepCursor(),
      fx: defaultFxAll(),
      trackMutes: defaultTrackMutes(),
      drumMutes: defaultDrumMutes(),
      subdivisions: defaultSubdivisions(),
      rootPitch: DEFAULT_ROOT,
      blueFlat3: false,
      blueFlat5: false,
      activeTrack: "drums",
    }),
}));

/**
 * Changing a track's subdivision resizes its grid. Armed cells are
 * proportionally remapped into the new cell count (nearest-position snap,
 * OR-collapse on collision) so the user's notes survive the edit.
 */
function applySubdivisionChange(
  state: InstrumentState,
  track: TrackId,
  flags: SubdivisionFlags,
  nextSteps: number,
): Partial<InstrumentState> {
  const nextSubdivisions: SubdivisionMap = {
    ...state.subdivisions,
    [track]: flags,
  };
  if (track === "drums") {
    const remapped = state.pattern.map((row) =>
      remapRowToSteps(row, nextSteps),
    );
    return {
      subdivisions: nextSubdivisions,
      pattern: remapped,
      step: { ...state.step, drums: 0 },
    };
  }
  const remapped = state.melodyGrids[track].map((row) =>
    remapRowToSteps(row, nextSteps),
  );
  return {
    subdivisions: nextSubdivisions,
    melodyGrids: {
      ...state.melodyGrids,
      [track]: remapped,
    },
    step: { ...state.step, [track]: 0 },
  };
}

export {
  emptyPattern,
  emptyMelodyGrid,
  emptyMelodyGrids,
  DEFAULT_FX,
  defaultStepCursor,
};
